import * as CookieConsent from 'vanilla-cookieconsent';
import { getDefaultStore } from 'jotai';
import { userAtom } from '../atoms/userAtom';

const ANALYTICS_COOKIE_CATEGORY = 'analytics';

const hasAnalyticsCookieConsent = (): boolean => {
  return CookieConsent.acceptedCategory(ANALYTICS_COOKIE_CATEGORY);
};

const getUserId = (): string | undefined => {
  if (!hasAnalyticsCookieConsent()) {
    return undefined;
  }
  const store = getDefaultStore();
  const user = store.get(userAtom);
  return user?.userId || undefined;
};

export enum EventName {
  GdprBanner = 'gdpr_banner',
  GdprInteraction = 'gdpr_interaction',
}

export enum InteractionType {
  Gdpr = 'gdpr',
}

export enum EventAction {
  Policy = 'policy',
  Open = 'open',
  Cancel = 'cancel',
  Analytics = 'analytics',
  DoNotSellMyInfo = 'do not sell my info',
  AcceptAll = 'accept all',
  RejectAll = 'reject all',
  AcceptCurrentSelection = 'accept current selection',
}

export enum Event {
  StandardInteractionWithClickText = 'standard interaction with click text',
}

export enum HitLocation {
  Modal = 'modal',
  OnPage = 'on page',
}

export enum InteractionTagName {
  GdprBannerPolicy = 'gdprBannerPolicy',
  GdprOpen = 'gdprOpen',
  GdprCancel = 'gdprCancel',
  GdprAnalytics = 'gdprAnalytics',
  GdprDoNotSellMyInfo = 'gdprDoNotSellMyInfo',
  GdprAcceptAll = 'gdprAcceptAll',
  GdprRejectAll = 'gdprRejectAll',
  GdprAcceptCurrentSelection = 'gdprAcceptCurrentSelection',
}

interface InteractionTagParams {
  event: Event;
  eventName: EventName;
  interactionType: InteractionType;
  eventAction: EventAction;
  clientClickText: string;
}

export const INTERACTION_TAG_CONFIG: Record<
  InteractionTagName,
  Omit<InteractionTagParams, 'clientClickText'>
> = {
  [InteractionTagName.GdprBannerPolicy]: {
    event: Event.StandardInteractionWithClickText,
    eventName: EventName.GdprBanner,
    interactionType: InteractionType.Gdpr,
    eventAction: EventAction.Policy,
  },
  [InteractionTagName.GdprOpen]: {
    event: Event.StandardInteractionWithClickText,
    eventName: EventName.GdprInteraction,
    interactionType: InteractionType.Gdpr,
    eventAction: EventAction.Open,
  },
  [InteractionTagName.GdprCancel]: {
    event: Event.StandardInteractionWithClickText,
    eventName: EventName.GdprInteraction,
    interactionType: InteractionType.Gdpr,
    eventAction: EventAction.Cancel,
  },
  [InteractionTagName.GdprAnalytics]: {
    event: Event.StandardInteractionWithClickText,
    eventName: EventName.GdprInteraction,
    interactionType: InteractionType.Gdpr,
    eventAction: EventAction.Analytics,
  },
  [InteractionTagName.GdprDoNotSellMyInfo]: {
    event: Event.StandardInteractionWithClickText,
    eventName: EventName.GdprInteraction,
    interactionType: InteractionType.Gdpr,
    eventAction: EventAction.DoNotSellMyInfo,
  },
  [InteractionTagName.GdprAcceptAll]: {
    event: Event.StandardInteractionWithClickText,
    eventName: EventName.GdprInteraction,
    interactionType: InteractionType.Gdpr,
    eventAction: EventAction.AcceptAll,
  },
  [InteractionTagName.GdprRejectAll]: {
    event: Event.StandardInteractionWithClickText,
    eventName: EventName.GdprInteraction,
    interactionType: InteractionType.Gdpr,
    eventAction: EventAction.RejectAll,
  },
  [InteractionTagName.GdprAcceptCurrentSelection]: {
    event: Event.StandardInteractionWithClickText,
    eventName: EventName.GdprInteraction,
    interactionType: InteractionType.Gdpr,
    eventAction: EventAction.AcceptCurrentSelection,
  },
};

export const pushToDataLayerWithReset = (data: Record<string, unknown>): void => {
  if (!globalThis.window?.dataLayer) return;

  globalThis.window.dataLayer.push(function (this: { reset: () => void }) {
    this.reset();
  });

  const userId = getUserId();
  const dataWithUserId = userId ? { ...data, user_id: userId } : data;

  globalThis.window.dataLayer.push(dataWithUserId);
};

/**
 * Track event data to GTM
 */
export const trackEventData = (
  interactionTagName: InteractionTagName,
  hitLocation: HitLocation,
  pathname: string,
  clientClickText: string,
  additionalData?: Record<string, unknown>
) => {
  const config = INTERACTION_TAG_CONFIG[interactionTagName];

  const eventData: Record<string, unknown> = {
    event: config.event,
    eventName: config.eventName,
    interactionType: config.interactionType,
    eventAction: config.eventAction,
    clientClickText,
    hitLocation,
    page: { pageinfo: { pathname } },
    ...additionalData,
  };

  pushToDataLayerWithReset(eventData);
};
